import {Injectable, inject, DestroyRef} from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { LorenDetailsOutletState } from "./interfaces/loren-details-outlet-state.interface";
import { LorenBreakpointObserverService } from "../loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: "root",
})
export class LorenDetailsOutletService {
  private readonly destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private breakpointObserverService = inject(LorenBreakpointObserverService);

  private subject = new BehaviorSubject<LorenDetailsOutletState>({
    width: 33,
    visible: false,
  });
  private lastState: LorenDetailsOutletState;
  private showDetailsOutletObservable = this.subject.asObservable();

  private isHandset: boolean;
  private isMedium: boolean;
  private isTablet: boolean;

  constructor() {
    this.breakpointObserverService
        .getHandsetStateSubscription()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => (this.isHandset = res));

    this.breakpointObserverService
        .getMediumStateSubscription()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => (this.isMedium = res));

    this.breakpointObserverService
        .getTabletStateSubscription()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => (this.isTablet = res));
  }

  getCurrentState() {
    return this.showDetailsOutletObservable;
  }

  getLastState() {
    return this.lastState;
  }

  toggleState(visibility: boolean, width: number = null) {
    width = width || this.getWidth();
    const newState: LorenDetailsOutletState = { visible: visibility, width };
    // handle case when new state has wider view than the old one
    if (
      this.lastState?.visible &&
      newState.visible &&
      this.lastState.width !== newState.width
    ) {
      this.subject.next({ visible: false, width });
      setTimeout(() => this.subject.next(newState), 500);
    } else {
      this.subject.next(newState);
    }

    this.lastState = newState;
  }

  navigate(route: string, currentRoute: string = null) {
    this.resetDetailsOutlet();

    setTimeout(() => {
      if (currentRoute) {
        this.router.navigate([
          currentRoute,
          {
            outlets: {
              details: `/${route}`,
            },
          },
        ]);
      } else {
        this.router.navigate([
          {
            outlets: {
              details: `${route}`,
            },
          },
        ]);
      }
    }, 0);
  }

  resetDetailsOutlet() {
    // TODO fix this with better solution for getting current primary route
    const currentRoute = this.router.url;
    const splitRoute = currentRoute.substring(1).split("/(");
    let resetRoute;

    if (splitRoute.length > 1) {
      resetRoute = splitRoute[0];
    } else {
      resetRoute = currentRoute.substring(1).split("(")[0];
    }

    this.router.navigate([
      "",
      {
        outlets: {
          details: null,
          primary: resetRoute,
        },
      },
    ]);
  }

  private getWidth() {
    return this.isHandset ? 85 : this.isMedium ? 45 : this.isTablet ? 50 : 28;
  }
}
